import React, { useState, useEffect } from 'react';
import { Link as RALink, useTranslate, usePermissions, useGetList, useGetIdentity } from 'react-admin';
import { Link } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment';

import IconCard from '../../common/IconCard';
import IconButton from '../../common/buttons/IconButton';
import HumanDateField from '../../common/fields/HumanDateField';
import WeeklyCard from '../../weeklies/WeeklyCard';
import MonthlyCard from '../../monthlies/MonthlyCard';
import PendingLeavesCard from '../PendingLeavesCard';
import NextLeavesCard from '../NextLeavesCard';
import MyProjectsCard from '../MyProjectsCard';
import MyLastIssuesCard from '../MyLastIssuesCard';
import TimeEntryBarChartCard from '../TimeEntryBarChartCard';
import NewSuggestionsCard from '../NewSuggestionsCard';
import LastNewsCard from '../LastNewsCard';
import WarningGrid from '../WarningGrid';

import customers from '../../customers';
import projects from '../../projects';

import { fetchDashboardStats } from '../../../services/api';


const cardTitleSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& > a': {
        color: (theme) => theme.palette.mode === 'dark' ? 'inherit' : theme.palette.secondary.main,
    }
};

const HomeTab = ({ warningTimesheet, warningWeekly, lastTimeEntries, lastWeeklies }) => {
    const [stats, setStats] = useState();
    const [now] = useState(moment());
    const { data: account } = useGetIdentity();
    const { permissions } = usePermissions();
    const translate = useTranslate();
    const { data: lastMonthlies } = useGetList(
        'monthlies',
        {
            pagination: { page: 1, perPage: 1 },
            sort: { field: 'date', order: 'DESC' }
        }
    );

    let warningMonthly = false;
    if (lastMonthlies?.length > 0) {
        const lastMonthlyDate = lastMonthlies[0].date;
        warningMonthly = now.diff(moment(lastMonthlyDate), 'days') >= 30;
    }

    useEffect(() => {
        const fetchStats = async () => {
            const data = await fetchDashboardStats();
            setStats(data);
        }

        fetchStats();
    }, []);

    return (
        <>
            <WarningGrid />
            <Grid container spacing={2} sx={{ marginTop: '15px' }}>
                {permissions?.includes('is_admin') && (
                    <Grid size={{ xl: 3, lg: 3, sm: 6, xs: 12 }}>
                        <RALink to="/customers">
                            <IconCard
                                title={translate('resources.customers.name', { smart_count: 2, _: "Customers" })}
                                value={stats?.customers}
                                icon={<customers.icon />}
                            />
                        </RALink>
                    </Grid>
                )}
                {permissions?.includes('is_admin') ? (
                    <Grid size={{ xl: 3, lg: 3, sm: 6, xs: 12 }}>
                        <RALink to="/projects">
                            <IconCard
                                title={translate('resources.projects.name', { smart_count: 2, _: "Projects" })}
                                value={stats?.projects}
                                icon={<projects.icon />}
                            />
                        </RALink>
                    </Grid>
                ) : (
                    <MyProjectsCard />
                )}
                <Grid size={{ xl: 3, lg: 3, sm: 6, xs: 12 }}>
                    <RALink to={`/leaves?filter={"user_id":${account?.id}}`}>
                        <IconCard title={translate('Remaining leave')} value={`${account?.remaining_leaves || 0}/26`} icon={<EventAvailableIcon />} />
                    </RALink>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ marginTop: '15px' }}>
                <PendingLeavesCard stats={stats} />
                <NextLeavesCard />
                {(warningTimesheet && lastTimeEntries?.length > 0) ? (
                    <Grid size={{ xl: 3, lg: 3, sm: 6, xs: 12 }}>
                        <RALink to='/time_entries/create'>
                            <IconCard
                                title={translate('Last time entry')}
                                value={new Date(lastTimeEntries[0].date).toLocaleDateString('fr-FR')}
                                icon={<AccessTimeIcon />}
                                warning
                                content={<img src="/img/rules/timesheet.jpg" alt="" width="100%" />}
                            />
                        </RALink>
                    </Grid>
                ) : (
                    account?.id && (
                        <Grid size={{ xl: 3, lg: 3, sm: 6, xs: 12 }}>
                            <TimeEntryBarChartCard />
                        </Grid>
                    )
                )}
                <MyLastIssuesCard />
                <LastNewsCard />
                <NewSuggestionsCard />
            </Grid>

            {(lastWeeklies?.length > 0 || lastMonthlies?.length > 0) && (
                <Grid container spacing={2} sx={{ marginTop: '15px' }}>
                    {lastMonthlies?.length > 0 && (
                        <Grid size={{ xl: 6, lg: 6, sm: 6, xs: 12 }}>
                            <MonthlyCard
                                record={lastMonthlies[0]}
                                title={
                                    <Box sx={cardTitleSx}>
                                        <RALink to={`/monthlies/${lastMonthlies[0].id}/show`} sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            {translate('Last monthly')} <HumanDateField source="date" record={lastMonthlies[0]} />
                                        </RALink>
                                        {(warningMonthly && permissions?.includes('is_admin')) && (
                                            <Box sx={{ margin: '0 10px' }}>
                                                <IconButton component={Link} to="/monthlies/create" icon={<AddIcon />} variant="outlined" color="warning">{translate('ra.action.create')}</IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                }
                                sx={{ height: 'auto' }}
                                collapse
                            />
                        </Grid>
                    )}
                    {lastWeeklies?.length > 0 && (
                        <Grid size={{ xl: 6, lg: 6, sm: 6, xs: 12 }}>
                            <WeeklyCard
                                record={lastWeeklies[0]}
                                title={
                                    <Box sx={cardTitleSx}>
                                        <RALink to={`/weeklies/${lastWeeklies[0].id}/show`} sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            {translate('Last weekly')} <HumanDateField source="date" record={lastWeeklies[0]} />
                                        </RALink>
                                        {warningWeekly && (
                                            <Box sx={{ margin: '0 10px' }}>
                                                <IconButton component={Link} to="/weeklies/create" icon={<AddIcon />} variant="outlined" color="warning">{translate('ra.action.create')}</IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                }
                                sx={{ height: 'auto' }}
                                collapse
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </>
    );
};

export default HomeTab;